import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext.js';
import './CreditTopUp.css';

// Load Stripe only when user runs top-up (dynamic import avoids loading Stripe on app init)
let stripePromise = null;
async function getStripePromise() {
  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) return null;
  if (!stripePromise) {
    const { loadStripe } = await import('@stripe/stripe-js');
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
}

const CREDIT_PACKAGES = [
  { credits: 100, price: 5, priceId: null, popular: false },
  { credits: 500, price: 20, priceId: null, popular: true },
  { credits: 1000, price: 35, priceId: null, popular: false },
  { credits: 2500, price: 75, priceId: null, popular: false },
  { credits: 5000, price: 140, priceId: null, popular: false },
];

export default function CreditTopUp({ onClose, onSuccess }) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const handleTopUp = async (pkg) => {
    if (!token) {
      setError('Please login to purchase credits');
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedPackage(pkg);

    try {
      // In production, you would create Stripe products/prices for credit packages
      // For now, we'll use a placeholder approach
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(apiUrl ? `${apiUrl}/credits/top-up-checkout` : '/credits/top-up-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          creditAmount: pkg.credits,
          priceId: pkg.priceId || `price_${pkg.credits}`, // Placeholder - create actual Stripe prices
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { sessionId } = await response.json();
      const stripe = await getStripePromise();
      if (!stripe) {
        alert("Stripe is not configured. Please contact support.");
        return;
      }
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (stripeError) {
        throw stripeError;
      }
    } catch (err) {
      console.error('Top-up error:', err);
      setError(err.message || 'Failed to process top-up');
      setLoading(false);
    }
  };

  return (
    <div className="credit-topup-overlay" onClick={onClose}>
      <div className="credit-topup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="credit-topup-header">
          <h2>💰 Top Up Credits</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="credit-topup-error">
            {error}
          </div>
        )}

        <div className="credit-topup-content">
          <p className="credit-topup-description">
            Purchase credits to use our AI tools. Credits never expire and can be used for any feature.
          </p>

          <div className="credit-packages">
            {CREDIT_PACKAGES.map((pkg) => (
              <div
                key={pkg.credits}
                className={`credit-package ${pkg.popular ? 'popular' : ''} ${selectedPackage?.credits === pkg.credits && loading ? 'loading' : ''}`}
                onClick={() => !loading && handleTopUp(pkg)}
              >
                {pkg.popular && <div className="popular-badge">Most Popular</div>}
                <div className="package-credits">{pkg.credits}</div>
                <div className="package-label">Credits</div>
                <div className="package-price">${pkg.price}</div>
                <div className="package-per-credit">
                  ${(pkg.price / pkg.credits).toFixed(3)} per credit
                </div>
                {loading && selectedPackage?.credits === pkg.credits && (
                  <div className="package-loading">Processing...</div>
                )}
              </div>
            ))}
          </div>

          <div className="credit-pricing-info">
            <h3>Per-Tool Pricing:</h3>
            <ul>
              <li>💬 Chat Message: <strong>1 credit</strong></li>
              <li>🔍 Web Search: <strong>2 credits</strong></li>
              <li>🖼️ Image Generation: <strong>10 credits</strong></li>
              <li>✏️ Image Editing: <strong>15 credits</strong></li>
              <li>🎬 Video Editing: <strong>50 credits</strong></li>
              <li>🌐 Website Generation: <strong>100 credits</strong></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

